/* eslint-disable max-lines */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import {
    OrderQuery as SourceQuery
} from 'SourceQuery/Order.query';
import { Field } from 'Util/Query';

export * from 'SourceQuery/Order.query';

/** @namespace Scandipwa/Query/Order/Query */
export class OrderQuery extends SourceQuery {
    _getTrackingField() {
        return new Field('tracking_numbers')
            .addFieldList(this._getTrackingFieldList());
    }

    _getTrackingFieldList() {
        return [
            'tracking_number'
        ];
    }

    // creating this one beside "_getOrderByIdField()" which queries ... "Customer" ?!
    getOrderDetailsByIdField(orderId) {
        return new Field('getOrderById')
            .addArgument('id', 'Int!', orderId)
            .addFieldList(this._getOrderDetailsItemsFields());
    }

    _getOrderDetailsItemsFields() {
        return [
            this._getBaseOrderInfoField(),
            this._getPaymentInfoField(),
            this._prepareShippingInfo(),
            this._getOrderProductsField()
        ];
    }

    _getBaseOrderInfoField() {
        return new Field('base_order_info')
            .addFieldList(this._getBaseOrderInfoFields());
    }

    _getBaseOrderInfoFields() {
        return [
            'id',
            'increment_id',
            'created_at',
            'status_label',
            'grand_total',
            'discount_amount',
            'cash_on_delivery_fee',
            'sub_total'
        ];
    }

    _getPaymentInfoField() {
        return new Field('payment_info')
            .addFieldList(this._getPaymentInfoFields());
    }

    _getPaymentInfoFields() {
        return [
            'method',
            this._getAdditionalInformationField()
        ];
    }

    _getAdditionalInformationField() {
        return new Field('additional_information')
            .addFieldList(this._getAdditionalInformationFields());
    }

    _getAdditionalInformationFields() {
        return [
            'bank',
            'method_title',
            'credit_type',
            'month',
            this._getCustomerInfoField()
        ];
    }

    _getCustomerInfoField() {
        return new Field('customer_info')
            .addFieldList(this._getCustomerInfoFields());
    }

    _getCustomerInfoFields() {
        return [
            'first_name',
            'last_name',
            'phone'
        ];
    }

    _prepareShippingInfo() {
        return new Field('shipping_info')
            .addFieldList(this._prepareShippingInfoFields());
    }

    _prepareOrderCustomerAddressInfo() {
        return new Field('shipping_address')
            .addFieldList(this._prepareOrderCustomerAddressInfoFields());
    }

    _prepareOrderCustomerAddressInfoFields() {
        return [
            'city',
            'company',
            'firstname',
            'lastname',
            'middlename',
            'telephone',
            'district',
            'house_number',
            'apartment_number',
            'postomat_code',
            'store_pickup_code',
            'post_office_code',
            'postcode',
            'street',
            'is_b2b',
            'region',
            'organizationname',
            'organizationbin',
            'organizationaddress',
            'organizationiic',
            'organizationbik'
        ];
    }

    _getOrderProductsField() {
        return new Field('order_products')
            .addFieldList(this._getOrderProductsFields());
    }

    _getOrderProductsFields() {
        return [
            ...this._getDefaultFields(),
            ...this._prepareImageFields(),
            this._prepareAttributes()
        ];
    }

    _prepareImageFields() {
        return [
            new Field('thumbnail')
                .addFieldList(this._prepareThumbnailFields()),
            new Field('small_image')
                .addFieldList(this._prepareSmallImageFields())
        ];
    }

    _prepareSmallImageFields() {
        return [
            'url',
            'label',
            'path'
        ];
    }

    _prepareThumbnailFields() {
        return [
            'url',
            'label',
            'path'
        ];
    }

    _prepareAttributes() {
        return new Field('s_attributes')
            .addFieldList(this._prepareAttributesFields());
    }

    _prepareAttributesFields() {
        return [
            'attribute_value',
            'attribute_code',
            'attribute_type',
            'attribute_label',
            this._getAttributeOptions()
        ];
    }

    _getAttributeOptions() {
        return new Field('attribute_options')
            .addFieldList(this._getAttributeOptionsFields());
    }

    _getAttributeOptionsFields() {
        return [
            'label',
            'value',
            new Field('swatch_data')
                .addField('value')
        ];
    }

    _getShipmentField() {
        return new Field('shipments')
            .addFieldList(this._getShipmentFieldList());
    }

    _getShipmentFieldList() {
        return [
            'shipment_id',
            'tracking_url',
            this._getTrackingField()
        ];
    }

    _getDefaultFields() {
        return [
            'id',
            'name',
            (new Field('short_description').addField('html')),
            'sku',
            'qty',
            'row_total',
            'original_price',
            'license_key',
            (new Field('attributes_info').addFieldList(this._getOrderAttributeInfoFields()))
        ];
    }

    _getOrderAttributeInfoFields() {
        return [
            'label',
            'option_id',
            'option_value',
            'value'
        ];
    }

    _prepareShippingInfoFields() {
        return [
            'shipping_method',
            'shipping_description',
            'shipping_amount',
            'shipping_incl_tax',
            'shipping_tax_amount',
            this._getShipmentField(),
            this._prepareOrderCustomerAddressInfo()
        ];
    }
}

export default new OrderQuery();
